/**
 * Canonical, deduplicated list of accepted QA/testing skills. This list is
 * mirrored on the backend (`/app/backend/skills.py`). If you edit one, edit
 * both.
 *
 * Rules:
 *  - Only entries in this list can be added to a seeker profile's skills or
 *    tools arrays.
 *  - Matching is case-insensitive.
 *  - When a user types a partial match, we surface the canonical spelling.
 */
export const CANONICAL_SKILLS = [
  // Test automation frameworks
  "Selenium WebDriver", "Playwright", "Cypress", "Appium", "Robot Framework",
  "Puppeteer", "WebdriverIO", "Karate Framework", "Serenity BDD", "Selenide",
  "Watir", "UFT (QTP)", "Ranorex", "TestComplete", "Katalon Studio",
  "Taiko", "Gauge",

  // Selenium ecosystem
  "Selenium Grid", "Selenium IDE", "Selenium 4", "Selenium Manager",
  "Selenium with Java", "Selenium with Python", "Selenium with JS/TS",
  "Selenium with C#",

  // Programming languages
  "Java", "Python", "JavaScript", "TypeScript", "C#", "C++", "PHP", "Ruby",
  "Kotlin", "Swift", "Go (Golang)", "Scala", "Shell Scripting",
  "Bash Scripting", "PowerShell",

  // API testing
  "API Testing", "Postman", "REST Assured", "SOAP UI", "Swagger", "GraphQL",
  "Bruno", "Insomnia", "ReadyAPI", "API Automation", "Web Services Testing",
  "gRPC Testing",

  // Performance testing
  "Performance Testing", "JMeter", "LoadRunner", "k6", "Gatling",
  "BlazeMeter", "Locust", "NeoLoad", "Load Testing", "Stress Testing",
  "Spike Testing", "Volume Testing", "Endurance Testing",

  // Unit test frameworks
  "TestNG", "JUnit", "Pytest", "NUnit", "Mocha", "Jest", "Jasmine", "Chai",
  "unittest", "xUnit",

  // BDD
  "BDD Frameworks", "Cucumber", "BDD", "Behave", "SpecFlow",

  // Mobile testing
  "Mobile Testing", "Espresso", "XCUITest", "Mobile Automation Testing",
  "Android Testing", "iOS Testing", "BrowserStack", "Sauce Labs",
  "LambdaTest",

  // CI/CD
  "Jenkins", "Docker", "Kubernetes", "GitHub Actions", "GitLab CI/CD",
  "Azure DevOps", "CircleCI", "TeamCity", "Bamboo", "Travis CI",
  "Bitbucket Pipelines", "ArgoCD",

  // Source control
  "Git", "GitHub", "GitLab", "Bitbucket", "SVN", "Azure Repos",

  // Databases
  "Database Skills", "SQL", "MySQL", "PostgreSQL", "Oracle SQL",
  "Microsoft SQL Server", "SQLite", "MongoDB", "Redis", "Cassandra",
  "MariaDB", "PL/SQL",

  // Cloud
  "Cloud Platforms", "AWS", "Microsoft Azure", "Google Cloud Platform (GCP)",
  "Oracle Cloud", "Firebase", "Cloud Testing",

  // Web tech
  "Web Technologies", "HTML", "CSS", "XML", "JSON", "DOM", "XPath",
  "CSS Selectors", "Regular Expressions",

  // Test management
  "Jira", "TestRail", "Zephyr", "Xray", "qTest", "HP ALM",
  "Azure Test Plans", "PractiTest", "TestLink", "Bugzilla", "Mantis",
  "YouTrack",

  // Reporting
  "Reporting Tools", "Allure Reports", "Extent Reports", "pytest-html",
  "HTML Reports", "Report Portal",

  // Security
  "Security Testing", "OWASP ZAP", "Burp Suite", "Vulnerability Testing",
  "Penetration Testing Basics",

  // Framework patterns
  "Automation Framework Skills", "Page Object Model (POM)",
  "Hybrid Framework", "Data Driven Framework", "Keyword Driven Framework",
  "Modular Framework", "Page Factory", "Singleton Design Pattern",
  "Factory Design Pattern", "Automation Framework Development",
  "Test Automation Strategy", "Parallel Execution", "Cross Browser Testing",

  // Manual & test types
  "Manual Testing", "Functional Testing", "Regression Testing",
  "Smoke Testing", "Sanity Testing", "Integration Testing", "System Testing",
  "User Acceptance Testing (UAT)", "Compatibility Testing", "GUI Testing",
  "Exploratory Testing", "Ad-hoc Testing", "Black Box Testing",
  "White Box Testing", "Gray Box Testing", "Acceptance Testing",
  "Test Planning", "Risk Based Testing",

  // OS / misc
  "Linux", "Unix", "Command Line Interface (CLI)", "YAML",
  "Microservices Testing", "Browser Developer Tools", "WebSocket Testing",
  "Kafka Testing", "RabbitMQ Testing",

  // AI / modern practice
  "AI Governance", "Test Strategy Creation", "Test Plan Creation",
  "Cursor AI", "Claude", "GitHub Copilot",
];

// Lower-cased set for O(1) membership checks
const CANONICAL_LOWER = new Set(CANONICAL_SKILLS.map((s) => s.toLowerCase()));

// Map lower -> canonical spelling so we can round-trip user input.
const CANONICAL_BY_LOWER = new Map(
  CANONICAL_SKILLS.map((s) => [s.toLowerCase(), s]),
);

/**
 * Given raw user input, return the canonical spelling if the value is in the
 * accepted list; otherwise return null.
 */
export function canonicalizeSkill(raw) {
  if (raw == null) return null;
  const key = String(raw).trim().toLowerCase();
  if (!key) return null;
  return CANONICAL_BY_LOWER.get(key) || null;
}

export function isAcceptedSkill(raw) {
  return canonicalizeSkill(raw) !== null;
}

/**
 * Returns up to `limit` suggestions matching the query.
 * - Requires at least 3 characters.
 * - Prefix matches rank first, then substring matches.
 */
export function suggestSkills(query, exclude = [], limit = 8) {
  const q = String(query || "").trim().toLowerCase();
  if (q.length < 3) return [];
  const excludeSet = new Set(exclude.map((s) => String(s).toLowerCase()));
  const prefix = [];
  const contains = [];
  for (const skill of CANONICAL_SKILLS) {
    const low = skill.toLowerCase();
    if (excludeSet.has(low)) continue;
    if (low.startsWith(q)) prefix.push(skill);
    else if (low.includes(q)) contains.push(skill);
    if (prefix.length >= limit) break;
  }
  return [...prefix, ...contains].slice(0, limit);
}

export { CANONICAL_LOWER };
